export const STRATOVAI_SESSION_EXPIRED_SAVE_MESSAGE =
  'Your Stratovai login session has expired. Please sign in again. Your changes were not saved.';

export const STRATOVAI_SESSION_EXPIRED_MESSAGE =
  'Your Stratovai login session has expired. Please sign in again.';

export const JIRA_TOKEN_ERROR_MESSAGE =
  'Your Jira API token is expired or invalid. Please update the Jira API token in Tokens & Security.';

export const OPENAI_TOKEN_ERROR_MESSAGE =
  'Your OpenAI API key is invalid. Please update the AI API key in Tokens & Security.';

export const GITHUB_CREDENTIAL_ERROR_MESSAGE =
  'Your GitHub repository credential is expired, revoked, or invalid. Please update the repository credential.';

export const GENERIC_CREDENTIAL_ERROR_MESSAGE =
  'A saved credential is expired or invalid. Please check the token fields in Tokens & Security.';

const FORBIDDEN_UI_FRAGMENTS = [
  'token_encrypted',
  'authorization:',
  'bearer ',
  'ghp_',
  'github_pat_',
  'sk-',
];

export function createSettingsApiError(status, detail, fallbackMessage) {
  const err = new Error(fallbackMessage);
  err.status = status;
  err.detail = detail;
  return err;
}

function normalizeDetail(error) {
  if (!error) return { text: '', code: null, credentialType: null };
  const detail = error.detail ?? error;
  if (typeof detail === 'string') {
    return { text: detail, code: null, credentialType: null };
  }
  if (typeof detail === 'object' && detail !== null) {
    return {
      text: detail.message || '',
      code: detail.code || null,
      credentialType: detail.credential_type || null,
    };
  }
  return { text: error.message || '', code: null, credentialType: null };
}

export function isStratovaiSessionExpired(error) {
  const { text, code, credentialType } = normalizeDetail(error);
  const status = error?.status;
  const lower = text.toLowerCase();

  if (code === 'auth_session_expired') return true;
  if (credentialType === 'stratovai_session' && (code === 'auth_invalid' || lower.includes('expired'))) {
    return true;
  }
  if (status === 401) return true;
  if (lower === 'token expired') return true;
  if (lower.includes('session has expired') || lower.includes('not authenticated')) return true;
  if (lower.includes('invalid token')) return true;
  return false;
}

export function getTokenErrorMessage(error, { unsavedChanges = false } = {}) {
  const { text, code, credentialType } = normalizeDetail(error);
  const lower = `${text} ${error?.message || ''}`.toLowerCase();

  if (code === 'auth_session_expired' || credentialType === 'stratovai_session') {
    return unsavedChanges ? STRATOVAI_SESSION_EXPIRED_SAVE_MESSAGE : STRATOVAI_SESSION_EXPIRED_MESSAGE;
  }

  if (isStratovaiSessionExpired(error)) {
    return unsavedChanges ? STRATOVAI_SESSION_EXPIRED_SAVE_MESSAGE : STRATOVAI_SESSION_EXPIRED_MESSAGE;
  }

  if (code === 'integration_token_invalid' || credentialType) {
    if (credentialType === 'jira_api_token') return JIRA_TOKEN_ERROR_MESSAGE;
    if (credentialType === 'ai_api_key' || credentialType === 'openai_api_key') return OPENAI_TOKEN_ERROR_MESSAGE;
    if (credentialType === 'github_repo_credential' || credentialType === 'github_pat') {
      return GITHUB_CREDENTIAL_ERROR_MESSAGE;
    }
  }

  if (lower.includes('jira')) return JIRA_TOKEN_ERROR_MESSAGE;
  if (lower.includes('openai') || lower.includes('ai_api_key') || lower.includes('ai api key')) {
    return OPENAI_TOKEN_ERROR_MESSAGE;
  }
  if (
    lower.includes('github')
    || lower.includes('repository credential')
    || lower.includes('repo credential')
    || lower.includes('automation/v1/repo-config')
  ) {
    return GITHUB_CREDENTIAL_ERROR_MESSAGE;
  }

  if (
    lower.includes('token expired')
    || lower.includes('expired or invalid')
    || lower.includes('invalid token')
    || lower.includes('credential')
  ) {
    return GENERIC_CREDENTIAL_ERROR_MESSAGE;
  }

  if (text) return text;
  if (error?.message && error.message !== 'Failed to save configuration') return error.message;
  return GENERIC_CREDENTIAL_ERROR_MESSAGE;
}

export function clearStratovaiSession() {
  localStorage.removeItem('stratovai_access_token');
  localStorage.removeItem('stratovai_refresh_token');
  localStorage.removeItem('authenticatedUser');
}

export function containsForbiddenTokenFragments(text) {
  if (!text) return false;
  const lower = String(text).toLowerCase();
  return FORBIDDEN_UI_FRAGMENTS.some((fragment) => lower.includes(fragment));
}
